import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, Lock, Unlock, Edit3, Trash2, Plus, Eye, Upload, FileText, CheckCircle, X, Square, CheckSquare, Copy, EyeOff, ChevronUp, ChevronDown, Building2, Package, FilterX, Settings, Columns, Calendar, Hash, MessageSquare } from 'lucide-react';
import { Device, supabase } from '../lib/supabase';
import { useAuditLog } from '../hooks/useAuditLog';
import { useLockMessages } from '../hooks/useLockMessages';
import { DeviceModal } from './DeviceModal';
import { ConfirmationModal } from './ConfirmationModal';
import { XCircle } from 'lucide-react';
import { format } from 'date-fns-tz';

interface DeviceListProps {
  devices: Device[];
  loading: boolean;
  onUpdateDevice: (id: string, updates: Partial<Device>) => Promise<{ success: boolean; error?: string }>;
  onDeleteDevice: (id: string) => Promise<{ success: boolean; error?: string }>;
  onAddDevice: (device: Omit<Device, 'id' | 'created_at' | 'updated_at'>) => Promise<{ success: boolean; error?: string }>;
  onBulkImport: (devices: Omit<Device, 'id' | 'created_at' | 'updated_at'>[]) => Promise<{ success: boolean; error?: string }>;
  initialFilter?: 'all' | 'locked' | 'unlocked';
}

export function DeviceList({ devices, loading, onUpdateDevice, onDeleteDevice, onAddDevice, onBulkImport, initialFilter = 'all' }: DeviceListProps) {
  const { logAction } = useAuditLog();
  const {
    messages: lockMessages,
    loading: lockMessagesLoading,
    createMessage,
    updateMessage,
    deleteMessage,
    setDefaultMessage
  } = useLockMessages();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'locked' | 'unlocked'>(initialFilter);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [originalDevice, setOriginalDevice] = useState<Device | null>(null); // Store original device for URL param comparison
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'view' | 'edit' | 'add'>('view');
  const [showUnlockCodes, setShowUnlockCodes] = useState<Record<string, boolean>>({});
  const [sortField, setSortField] = useState<'owner' | 'lockAppStatus' | 'serial' | 'deviceName' | 'status' | 'ictStatus' | 'createdAt' | 'updatedAt' | 'id' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Advanced filtering state
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [columnFilters, setColumnFilters] = useState({
    serial: '',
    deviceName: '',
    owner: '',
    status: 'all' as 'all' | 'locked' | 'unlocked',
    ictStatus: 'all' as 'all' | 'field' | 'ict',
    lockAppStatus: 'all' as 'all' | 'run' | 'not-run'
  });
  const [activeColumnFilter, setActiveColumnFilter] = useState<string | null>(null);
  
  // Bulk selection state
  const [selectedDevices, setSelectedDevices] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // Confirmation modal state
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    type: 'lock' | 'unlock' | 'delete' | 'bulk-lock' | 'bulk-unlock' | 'bulk-delete' | 'remove-from-ict';
    device?: Device;
    onConfirm: () => void;
  }>({
    isOpen: false,
    type: 'delete',
    onConfirm: () => {}
  });
  const [actionLoading, setActionLoading] = useState(false);
  
  const [lockAppStatuses, setLockAppStatuses] = useState<Record<string, { hasRun: boolean; timestamp: string | null; formatted?: string }>>({});

  const [isLockMessageManagerOpen, setIsLockMessageManagerOpen] = useState(false);
  const [editingLockMessageId, setEditingLockMessageId] = useState<number | null>(null);
  const [lockMessageForm, setLockMessageForm] = useState({
    title: '',
    line1: '',
    line2: '',
    line3: '',
    footer: ''
  });
  const [lockMessageSaving, setLockMessageSaving] = useState(false);

  const previewTitle = lockMessageForm.title.trim();
  const previewLine1 = lockMessageForm.line1.trim();
  const previewLine2 = lockMessageForm.line2.trim();
  const previewLine3 = lockMessageForm.line3.trim();
  const previewFooter = lockMessageForm.footer.trim();

  const [lockConfigModal, setLockConfigModal] = useState<{ isOpen: boolean; deviceIds: string[] }>({
    isOpen: false,
    deviceIds: []
  });
  const [selectedLockMessageId, setSelectedLockMessageId] = useState<number | ''>('');
  const [lockConfigLoading, setLockConfigLoading] = useState(false);

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    createdAt: false,   // hidden by default
    updatedAt: false,   // hidden by default (optional)
    id: false           // hidden by default (optional)
  });
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [urlParamsHandled, setUrlParamsHandled] = useState(false);

  // Update filter when initialFilter changes
  useEffect(() => {
    setFilterStatus(initialFilter);
  }, [initialFilter]);

  // Check for refresh parameter and trigger refetch
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('refresh') === 'true') {
      // Remove the refresh parameter from URL
      urlParams.delete('refresh');
      const newUrl = `${window.location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
      
      // Force a page refresh to reload the device list
      window.location.reload();
    }
  }, []);

  // Parse comma-separated serials in search
  const parseSearchTerms = (term: string): string[] => {
    return term.split(',').map(t => t.trim().toUpperCase()).filter(t => t.length > 0);
  };

  // Handle sorting
  const handleSort = (field: 'owner' | 'lockAppStatus' | 'serial' | 'deviceName' | 'status' | 'ictStatus' | 'createdAt' | 'updatedAt' | 'id') => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSearchTerm('');
    setFilterStatus('all');
    setColumnFilters({
      serial: '',
      deviceName: '',
      owner: '',
      status: 'all',
      ictStatus: 'all',
      lockAppStatus: 'all'
    });
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return searchTerm || 
           filterStatus !== 'all' || 
           columnFilters.serial || 
           columnFilters.deviceName || 
           columnFilters.owner || 
           columnFilters.status !== 'all' || 
           columnFilters.ictStatus !== 'all' || 
           columnFilters.lockAppStatus !== 'all';
  };

  const filteredDevices = devices.filter(device => {
    const searchTerms = parseSearchTerms(searchTerm);
    const matchesSearch = searchTerms.length === 0 || searchTerms.some(term =>
      device.serial_number.toUpperCase().includes(term) ||
      device.owner_name.toLowerCase().includes(term.toLowerCase()) ||
      device.device_name.toLowerCase().includes(term.toLowerCase())
    );
    
    const matchesFilter = filterStatus === 'all' || 
                         (filterStatus === 'locked' && device.is_locked) ||
                         (filterStatus === 'unlocked' && !device.is_locked);

    // Advanced column filters
    const matchesSerial = !columnFilters.serial || 
                         device.serial_number.toLowerCase().includes(columnFilters.serial.toLowerCase());
    
    const matchesDeviceName = !columnFilters.deviceName || 
                             device.device_name.toLowerCase().includes(columnFilters.deviceName.toLowerCase());
    
    const matchesOwner = !columnFilters.owner || 
                        device.owner_name.toLowerCase().includes(columnFilters.owner.toLowerCase());
    
    const matchesStatus = columnFilters.status === 'all' || 
                         (columnFilters.status === 'locked' && device.is_locked) ||
                         (columnFilters.status === 'unlocked' && !device.is_locked);
    
    const matchesIctStatus = columnFilters.ictStatus === 'all' ||
                            (columnFilters.ictStatus === 'ict' && device.with_ict) ||
                            (columnFilters.ictStatus === 'field' && !device.with_ict);
    
    const deviceHasRun = lockAppStatuses[device.serial_number]?.hasRun || false;
    const matchesLockAppStatus = columnFilters.lockAppStatus === 'all' ||
                                (columnFilters.lockAppStatus === 'run' && deviceHasRun) ||
                                (columnFilters.lockAppStatus === 'not-run' && !deviceHasRun);
    
    return matchesSearch && matchesFilter && matchesSerial && matchesDeviceName && 
           matchesOwner && matchesStatus && matchesIctStatus && matchesLockAppStatus;
  }).sort((a, b) => {
    if (!sortField) return 0;
    
    let comparison = 0;
    
    if (sortField === 'owner') {
      comparison = a.owner_name.localeCompare(b.owner_name);
    } else if (sortField === 'serial') {
      comparison = a.serial_number.localeCompare(b.serial_number);
    } else if (sortField === 'deviceName') {
      comparison = a.device_name.localeCompare(b.device_name);
    } else if (sortField === 'status') {
      comparison = (a.is_locked ? 1 : 0) - (b.is_locked ? 1 : 0);
    } else if (sortField === 'ictStatus') {
      comparison = (a.with_ict ? 1 : 0) - (b.with_ict ? 1 : 0);
    } else if (sortField === 'lockAppStatus') {
      const aStatus = lockAppStatuses[a.serial_number];
      const bStatus = lockAppStatuses[b.serial_number];

      // Use raw timestamp for sorting
      const aTime = aStatus?.timestamp ? new Date(aStatus.timestamp).getTime() : 0;
      const bTime = bStatus?.timestamp ? new Date(bStatus.timestamp).getTime() : 0;

      comparison = bTime - aTime;
    } else if (sortField === 'createdAt') {
      comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    } else if (sortField === 'updatedAt') {
      comparison = new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime();
    } else if (sortField === 'id') {
      comparison = a.id.localeCompare(b.id);
    }
    
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  // Handle bulk selection
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedDevices(new Set());
    } else {
      setSelectedDevices(new Set(filteredDevices.map(d => d.id)));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectDevice = (deviceId: string) => {
    const newSelected = new Set(selectedDevices);
    if (newSelected.has(deviceId)) {
      newSelected.delete(deviceId);
    } else {
      newSelected.add(deviceId);
    }
    setSelectedDevices(newSelected);
    setSelectAll(newSelected.size === filteredDevices.length);
  };

  // Clear selection when search/filter changes
  useEffect(() => {
    setSelectedDevices(new Set());
    setSelectAll(false);
  }, [searchTerm, filterStatus, columnFilters]);

  const resetLockMessageForm = () => {
    setEditingLockMessageId(null);
    setLockMessageForm({
      title: '',
      line1: '',
      line2: '',
      line3: '',
      footer: ''
    });
  };

  const openLockConfigModal = (deviceIds: string[]) => {
    const defaultTemplate = lockMessages.find((message) => message.is_default) || lockMessages[0];
    setSelectedLockMessageId(defaultTemplate ? defaultTemplate.id : '');
    setLockConfigModal({
      isOpen: true,
      deviceIds
    });
  };

  const handleLockWithTemplate = async () => {
    if (!selectedLockMessageId) return;

    const selectedTemplate = lockMessages.find((message) => message.id === selectedLockMessageId);
    const targetDevices = devices.filter((device) => lockConfigModal.deviceIds.includes(device.id));
    if (targetDevices.length === 0) return;

    setLockConfigLoading(true);
    for (const device of targetDevices) {
      const result = await onUpdateDevice(device.id, {
        is_locked: true,
        lock_message_id: selectedLockMessageId
      });

      await logAction(
        targetDevices.length > 1 ? 'bulk_lock_devices' : 'lock_device',
        'device_management',
        {
          resourceType: 'device',
          resourceId: device.id,
          details: {
            device_name: device.device_name,
            serial_number: device.serial_number,
            owner_name: device.owner_name,
            previous_status: device.is_locked ? 'locked' : 'unlocked',
            new_status: 'locked',
            lock_message_id: selectedLockMessageId,
            lock_message_title: selectedTemplate?.title,
            bulk_operation: targetDevices.length > 1,
            total_devices: targetDevices.length > 1 ? targetDevices.length : undefined
          },
          success: result.success,
          errorMessage: result.error
        }
      );
    }

    setLockConfigLoading(false);
    setLockConfigModal({ isOpen: false, deviceIds: [] });
    setSelectedDevices(new Set());
    setSelectAll(false);
  };

  const handleSaveLockMessage = async () => {
    if (!lockMessageForm.title.trim() || !lockMessageForm.line1.trim()) return;

    setLockMessageSaving(true);
    const payload = {
      title: lockMessageForm.title,
      line1: lockMessageForm.line1,
      line2: lockMessageForm.line2,
      line3: lockMessageForm.line3,
      footer: lockMessageForm.footer
    };

    if (editingLockMessageId) {
      const result = await updateMessage(editingLockMessageId, payload);
      await logAction('update_lock_message', 'device_management', {
        resourceType: 'lock_message',
        resourceId: editingLockMessageId.toString(),
        details: payload,
        success: result.success,
        errorMessage: result.error
      });
    } else {
      const result = await createMessage(payload);
      await logAction('create_lock_message', 'device_management', {
        resourceType: 'lock_message',
        resourceId: result.success && result.data ? result.data.id.toString() : undefined,
        details: payload,
        success: result.success,
        errorMessage: result.error
      });
    }

    setLockMessageSaving(false);
    resetLockMessageForm();
  };

  const handleEditLockMessage = (messageId: number) => {
    const template = lockMessages.find((item) => item.id === messageId);
    if (!template) return;

    setEditingLockMessageId(template.id);
    setLockMessageForm({
      title: template.title,
      line1: template.line1,
      line2: template.line2 || '',
      line3: template.line3 || '',
      footer: template.footer || ''
    });
  };

  const handleDeleteLockMessage = async (messageId: number) => {
    const confirmed = window.confirm('Delete this lock message template?');
    if (!confirmed) return;

    const result = await deleteMessage(messageId);
    await logAction('delete_lock_message', 'device_management', {
      resourceType: 'lock_message',
      resourceId: messageId.toString(),
      success: result.success,
      errorMessage: result.error
    });

    if (editingLockMessageId === messageId) {
      resetLockMessageForm();
    }
  };

  const handleSetDefaultLockMessage = async (messageId: number) => {
    const result = await setDefaultMessage(messageId);
    await logAction('set_default_lock_message', 'device_management', {
      resourceType: 'lock_message',
      resourceId: messageId.toString(),
      success: result.success,
      errorMessage: result.error
    });
  };

  const handleToggleLock = (device: Device) => {
    if (!device.is_locked) {
      openLockConfigModal([device.id]);
      return;
    }

    setConfirmationModal({
      isOpen: true,
      type: device.is_locked ? 'unlock' : 'lock',
      device,
      onConfirm: async () => {
        setActionLoading(true);
        const result = await onUpdateDevice(device.id, { is_locked: !device.is_locked });
        
        // Log the action
        await logAction(
          device.is_locked ? 'unlock_device' : 'lock_device',
          'device_management',
          {
            resourceType: 'device',
            resourceId: device.id,
            details: {
              device_name: device.device_name,
              serial_number: device.serial_number,
              owner_name: device.owner_name,
              previous_status: device.is_locked ? 'locked' : 'unlocked',
              new_status: device.is_locked ? 'unlocked' : 'locked'
            },
            success: result.success,
            errorMessage: result.error
          }
        );
        
        setActionLoading(false);
        setConfirmationModal(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  const handleDelete = (device: Device) => {
    setConfirmationModal({
      isOpen: true,
      type: 'delete',
      device,
      onConfirm: async () => {
        setActionLoading(true);
        const result = await onDeleteDevice(device.id);
        
        // Log the action
        await logAction(
          'delete_device',
          'device_management',
          {
            resourceType: 'device',
            resourceId: device.id,
            details: {
              device_name: device.device_name,
              serial_number: device.serial_number,
              owner_name: device.owner_name
            },
            success: result.success,
            errorMessage: result.error
          }
        );
        
        setActionLoading(false);
        setConfirmationModal(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  const handleToggleICT = async (device: Device) => {
    if (!device.with_ict) {
      // Moving to ICT - show modal for details
      setSelectedDevice(device);
      setModalMode('edit');
      setIsModalOpen(true);
    } else {
      // Moving back to field - show confirmation modal to prevent accidental data loss
      setConfirmationModal({
        isOpen: true,
        type: 'remove-from-ict',
        device,
        onConfirm: async () => {
          setActionLoading(true);
          
          // Store the stack position info before removal for repositioning other devices
          const removedStackNumber = device.details?.stack_number;
          const removedPosition = device.details?.position_in_stack;
          
          // Remove the device from ICT first
          const result = await onUpdateDevice(device.id, { 
            with_ict: false, 
            details: null 
          });
          
          // If removal was successful and device had a position, batch update other devices in the same stack
          if (result.success && removedStackNumber && removedPosition) {
            // Find all other devices in the same stack with positions higher than the removed device
            const devicesInSameStack = devices.filter(d => 
              d.id !== device.id && 
              d.with_ict && 
              d.details?.stack_number === removedStackNumber &&
              d.details?.position_in_stack &&
              d.details.position_in_stack > removedPosition
            );
            
            // Batch update all devices at once using Promise.all to avoid multiple re-renders
            if (devicesInSameStack.length > 0) {
              const repositionPromises = devicesInSameStack.map(async (stackDevice) => {
                if (stackDevice.details?.position_in_stack) {
                  const newPosition = stackDevice.details.position_in_stack - 1;
                  
                  // Update device position
                  const updateResult = await onUpdateDevice(stackDevice.id, {
                    details: {
                      ...stackDevice.details,
                      position_in_stack: newPosition
                    }
                  });
                  
                  // Log the automatic repositioning
                  await logAction(
                    'edit_device',
                    'device_management',
                    {
                      resourceType: 'device',
                      resourceId: stackDevice.id,
                      details: {
                        device_name: stackDevice.device_name,
                        serial_number: stackDevice.serial_number,
                        owner_name: stackDevice.owner_name,
                        action: 'auto_reposition_in_stack',
                        stack_number: removedStackNumber,
                        old_position: stackDevice.details.position_in_stack,
                        new_position: newPosition,
                        reason: `Auto-repositioned due to removal of device ${device.serial_number} from position ${removedPosition}`
                      },
                      success: updateResult.success,
                      errorMessage: updateResult.error
                    }
                  );
                  
                  return updateResult;
                }
                return { success: false, error: 'No position data' };
              });
              
              // Wait for all repositioning updates to complete
              await Promise.all(repositionPromises);
            }
          }
          
          // Log the main removal action
          await logAction(
            'edit_device',
            'device_management',
            {
              resourceType: 'device',
              resourceId: device.id,
              details: {
                device_name: device.device_name,
                serial_number: device.serial_number,
                owner_name: device.owner_name,
                action: 'remove_from_ict',
                previous_ict_details: device.details,
                devices_repositioned: removedStackNumber && removedPosition ? 
                  devices.filter(d => 
                    d.id !== device.id && 
                    d.with_ict && 
                    d.details?.stack_number === removedStackNumber &&
                    d.details?.position_in_stack &&
                    d.details.position_in_stack > removedPosition
                  ).length : 0
              },
              success: result.success,
              errorMessage: result.error
            }
          );
          
          setActionLoading(false);
          setConfirmationModal(prev => ({ ...prev, isOpen: false }));
        }
      });
    }
  };

  const handleBulkAction = (action: 'lock' | 'unlock' | 'delete') => {
    const selectedDevicesList = devices.filter(d => selectedDevices.has(d.id));

    if (action === 'lock') {
      openLockConfigModal(selectedDevicesList.map((device) => device.id));
      return;
    }
    
    setConfirmationModal({
      isOpen: true,
      type: `bulk-${action}` as 'bulk-lock' | 'bulk-unlock' | 'bulk-delete',
      onConfirm: async () => {
        setActionLoading(true);
        
        if (action === 'delete') {
          for (const device of selectedDevicesList) {
            const result = await onDeleteDevice(device.id);
            // Log each deletion
            await logAction(
              'bulk_delete_devices',
              'device_management',
              {
                resourceType: 'device',
                resourceId: device.id,
                details: {
                  device_name: device.device_name,
                  serial_number: device.serial_number,
                  owner_name: device.owner_name,
                  bulk_operation: true,
                  total_devices: selectedDevicesList.length
                },
                success: result.success,
                errorMessage: result.error
              }
            );
          }
        } else {
          const isLocked = action === 'lock';
          for (const device of selectedDevicesList) {
            const result = await onUpdateDevice(device.id, { is_locked: isLocked });
            // Log each lock/unlock operation
            await logAction(
              action === 'lock' ? 'bulk_lock_devices' : 'bulk_unlock_devices',
              'device_management',
              {
                resourceType: 'device',
                resourceId: device.id,
                details: {
                  device_name: device.device_name,
                  serial_number: device.serial_number,
                  owner_name: device.owner_name,
                  previous_status: device.is_locked ? 'locked' : 'unlocked',
                  new_status: isLocked ? 'locked' : 'unlocked',
                  bulk_operation: true,
                  total_devices: selectedDevicesList.length
                },
                success: result.success,
                errorMessage: result.error
              }
            );
          }
        }
        
        setSelectedDevices(new Set());
        setSelectAll(false);
        setActionLoading(false);
        setConfirmationModal(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  const handleEdit = (device: Device) => {
    setSelectedDevice({
      ...device,
      with_ict: device.with_ict || false,
      details: device.details || null
    });
    setModalMode('edit');
    setIsModalOpen(true);
  };

  const handleView = (device: Device) => {
    setSelectedDevice({
      ...device,
      with_ict: device.with_ict || false,
      details: device.details || null
    });
    setModalMode('view');
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    setSelectedDevice(null);
    setModalMode('add');
    setIsModalOpen(true);
  };

  const handleSave = async (deviceData: Omit<Device, 'id' | 'created_at' | 'updated_at'>) => {
    if (modalMode === 'add') {
      const result = await onAddDevice(deviceData);
      
      // Log the action
      await logAction(
        'create_device',
        'device_management',
        {
          resourceType: 'device',
          details: {
            device_name: deviceData.device_name,
            serial_number: deviceData.serial_number,
            owner_name: deviceData.owner_name,
            is_locked: deviceData.is_locked
          },
          success: result.success,
          errorMessage: result.error
        }
      );
      
      if (result.success) {
        setIsModalOpen(false);
      }
      return result;
    } else if (modalMode === 'edit' && selectedDevice) {
      const result = await onUpdateDevice(selectedDevice.id, deviceData);
      
      // Log the action
      await logAction(
        'edit_device',
        'device_management',
        {
          resourceType: 'device',
          resourceId: selectedDevice.id,
          details: {
            device_name: deviceData.device_name,
            serial_number: deviceData.serial_number,
            owner_name: deviceData.owner_name,
            is_locked: deviceData.is_locked,
            previous_data: {
              device_name: selectedDevice.device_name,
              serial_number: selectedDevice.serial_number,
              owner_name: selectedDevice.owner_name,
              is_locked: selectedDevice.is_locked
            }
          },
          success: result.success,
          errorMessage: result.error
        }
      );
      
      if (result.success) {
        setIsModalOpen(false);
      }
      return result;
    }
    return { success: false, error: 'Invalid operation' };
  };

  const getConfirmationMessage = () => {
    const { type, device } = confirmationModal;
    const count = selectedDevices.size;
    
    switch (type) {
      case 'lock':
        return `Are you sure you want to lock device "${device?.serial_number}"? This will prevent the device from being used.`;
      case 'unlock':
        return `Are you sure you want to unlock device "${device?.serial_number}"? This will allow the device to be used normally.`;
      case 'delete':
        return `Are you sure you want to delete device "${device?.serial_number}"? This action cannot be undone and all associated data will be permanently removed.`;
      case 'remove-from-ict':
        const stackInfo = device?.details?.stack_number && device?.details?.position_in_stack ?
          `\n\nNote: This device is currently in Stack ${device.details.stack_number}, Position ${device.details.position_in_stack}. Other devices in higher positions will automatically be moved down to fill the gap.` : '';
        return `Are you sure you want to move device "${device?.serial_number}" back to the field? This will permanently delete all ICT storage information including:\n\n• Stack number and position\n• Accessory information (charger, bag)\n• Condition notes\n• Storage dates${stackInfo}\n\nThis action cannot be undone.`;
      case 'bulk-lock':
        return `Are you sure you want to lock ${count} selected device${count > 1 ? 's' : ''}? This will prevent ${count > 1 ? 'these devices' : 'this device'} from being used.`;
      case 'bulk-unlock':
        return `Are you sure you want to unlock ${count} selected device${count > 1 ? 's' : ''}? This will allow ${count > 1 ? 'these devices' : 'this device'} to be used normally.`;
      case 'bulk-delete':
        return `Are you sure you want to delete ${count} selected device${count > 1 ? 's' : ''}? This action cannot be undone and all associated data will be permanently removed.`;
      default:
        return 'Are you sure you want to perform this action?';
    }
  };

  const getConfirmationTitle = () => {
    const { type } = confirmationModal;
    const count = selectedDevices.size;
    
    switch (type) {
      case 'lock':
        return 'Lock Device';
      case 'unlock':
        return 'Unlock Device';
      case 'delete':
        return 'Delete Device';
      case 'remove-from-ict':
        return 'Remove from ICT Storage';
      case 'bulk-lock':
        return `Lock ${count} Device${count > 1 ? 's' : ''}`;
      case 'bulk-unlock':
        return `Unlock ${count} Device${count > 1 ? 's' : ''}`;
      case 'bulk-delete':
        return `Delete ${count} Device${count > 1 ? 's' : ''}`;
      default:
        return 'Confirm Action';
    }
  };

  // Column Filter Component
  const ColumnFilter = ({ 
    column, 
    type = 'search', 
    options = [],
    placeholder = 'Filter...'
  }: { 
    column: keyof typeof columnFilters; 
    type?: 'search' | 'select';
    options?: { value: string; label: string }[];
    placeholder?: string;
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    const currentValue = columnFilters[column];
    const hasFilter = type === 'search' ? currentValue : currentValue !== 'all';

    return (
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`p-1 rounded-lg transition-all duration-200 ${
            hasFilter 
              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' 
              : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
          }`}
        >
          <Filter className="w-3 h-3" />
        </button>
        
        {isOpen && (
          <>
            <div 
              className="fixed inset-0 z-10" 
              onClick={() => setIsOpen(false)}
            />
            <div className="absolute top-8 left-0 z-20 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 p-3 min-w-[200px]">
              {type === 'search' ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    placeholder={placeholder}
                    value={currentValue as string}
                    onChange={(e) => setColumnFilters(prev => ({ ...prev, [column]: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                    autoFocus
                  />
                  {currentValue && (
                    <button
                      onClick={() => setColumnFilters(prev => ({ ...prev, [column]: '' }))}
                      className="w-full text-left px-2 py-1 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                    >
                      Clear filter
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-1">
                  {options.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setColumnFilters(prev => ({ ...prev, [column]: option.value }));
                        setIsOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${
                        currentValue === option.value
                          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                          : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  useEffect(() => {
    const fetchLockAppStatuses = async () => {
      const serialNumbers = devices.map(device => device.serial_number);

      const { data, error } = await supabase
  .from('lock_app_runs')
  .select('serial_number, timestamp')
  .in('serial_number', serialNumbers)
  .order('timestamp', { ascending: false });

      if (error) {
        console.error('Error fetching lock app statuses:', error);
        return;
      }

   const statuses = data.reduce<Record<string, { hasRun: boolean; timestamp: string | null; formatted?: string }>>((acc, run) => {
  if (!acc[run.serial_number]) {
    acc[run.serial_number] = {
      hasRun: true,
      timestamp: run.timestamp ?? null, // store raw timestamp
      formatted: run.timestamp ? format(new Date(run.timestamp), 'dd/MM/yy hh:mm a', { timeZone: 'Australia/Adelaide' }) : undefined
    };
  }
  return acc;
}, {});

      setLockAppStatuses(statuses);
    };

    if (devices.length > 0) {
      fetchLockAppStatuses();
    }
  }, [devices]);

  // Parse URL query parameters for pre-filling device data
  useEffect(() => {
    const handleUrlChange = () => {
      // Only handle URL params once and when devices are loaded
      if (urlParamsHandled || devices.length === 0) return;
      
      const queryParams = new URLSearchParams(window.location.search);
      const addDevice = queryParams.get('adddevice');
      const serial = queryParams.get('serial');
      const deviceName = queryParams.get('devicename');
      const owner = queryParams.get('owner');
      const locked = queryParams.get('locked');

      if (addDevice !== null) {
        // Mark as handled to prevent re-running
        setUrlParamsHandled(true);
        
        // Check if a device with this serial number already exists
        const existingDevice = devices.find(device => 
          device.serial_number.toLowerCase() === (serial || '').toLowerCase()
        );

        if (existingDevice && serial) {
          // Device exists - open in edit mode with updated values
          setOriginalDevice(existingDevice); // Store original for comparison
          
          // Create the updated device with URL parameter values
          const updatedDevice = {
            ...existingDevice,
            // Always apply URL parameters when provided, regardless of current values
            device_name: deviceName !== null ? deviceName : existingDevice.device_name,
            owner_name: owner !== null ? owner : existingDevice.owner_name,
            is_locked: locked !== null ? (locked === 'true' || locked === '1') : existingDevice.is_locked,
            with_ict: existingDevice.with_ict || false,
            details: existingDevice.details || null
          };
          
          setSelectedDevice(updatedDevice);
          setModalMode('edit');
          setIsModalOpen(true);
        } else {
          // Device doesn't exist - create new one
          setOriginalDevice(null); // No original device for new devices
          setSelectedDevice({
            id: '',
            serial_number: serial || '',
            device_name: deviceName || '',
            owner_name: owner || '',
            is_locked: locked ? (locked === 'true' || locked === '1') : false,
            unlock_code: '',
            note: '',
            with_ict: false,
            details: null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
          setModalMode('add');
          setIsModalOpen(true);
        }
      }
    };

    handleUrlChange(); // Run on initial load

    window.addEventListener('popstate', handleUrlChange);
    return () => {
      window.removeEventListener('popstate', handleUrlChange);
    };
  }, [devices, urlParamsHandled]); // Add devices dependency so it runs when devices are loaded

  if (loading) {
    return (
      <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-2xl p-6 border border-slate-200/60 dark:border-slate-700/60 shadow-lg">
        <div className="animate-pulse space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Device Management</h2>
          <p className="text-slate-600 dark:text-slate-400">Manage and monitor all registered devices</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setIsLockMessageManagerOpen(true)}
            className="flex items-center space-x-2 bg-gradient-to-r from-amber-600 to-orange-600 text-white px-4 py-2 rounded-xl hover:from-amber-700 hover:to-orange-700 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <MessageSquare className="w-4 h-4" />
            <span>Lock Messages</span>
          </button>
          <Link
            to="/bulk-import"
            className="flex items-center space-x-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-4 py-2 rounded-xl hover:from-emerald-700 hover:to-teal-700 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <Upload className="w-4 h-4" />
            <span>Bulk Import</span>
          </Link>
          <button
            onClick={handleAdd}
            className="flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <Plus className="w-4 h-4" />
            <span>Add Device</span>
          </button>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedDevices.size > 0 && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200/60 dark:border-blue-800/50 rounded-xl p-4 shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                <CheckSquare className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="text-blue-800 dark:text-blue-300 font-bold">
                  {selectedDevices.size} device{selectedDevices.size > 1 ? 's' : ''} selected
                </span>
                <p className="text-blue-600 dark:text-blue-400 text-sm">Choose an action to apply</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleBulkAction('unlock')}
                className="flex items-center space-x-1 px-3 py-2 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white rounded-lg transition-all duration-200 text-sm font-medium shadow-lg hover:shadow-xl"
              >
                <Unlock className="w-4 h-4" />
                <span>Unlock</span>
              </button>
              <button
                onClick={() => handleBulkAction('lock')}
                className="flex items-center space-x-1 px-3 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-lg transition-all duration-200 text-sm font-medium shadow-lg hover:shadow-xl"
              >
                <Lock className="w-4 h-4" />
                <span>Lock</span>
              </button>
              <button
                onClick={() => handleBulkAction('delete')}
                className="flex items-center space-x-1 px-3 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-lg transition-all duration-200 text-sm font-medium shadow-lg hover:shadow-xl"
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete</span>
              </button>
              <button
                onClick={() => {
                  setSelectedDevices(new Set());
                  setSelectAll(false);
                }}
                className="p-2 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-all duration-200"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-2xl p-6 border border-slate-200/60 dark:border-slate-700/60 shadow-lg">
        <div className="space-y-4">
          {/* Basic Filters Row */}
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-slate-500">
                <Search className="w-5 h-5" />
              </div>
              <input
                type="text"
                placeholder="Search devices, owners, or comma-separated serials (ABC123, DEF456)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/70 dark:bg-slate-700/70 backdrop-blur-sm text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 shadow-lg font-medium"
              />
            </div>
            <div className="flex items-center space-x-3">
              <Filter className="w-5 h-5 text-slate-500 dark:text-slate-400" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as 'all' | 'locked' | 'unlocked')}
                className="border border-slate-200 dark:border-slate-600 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/70 dark:bg-slate-700/70 backdrop-blur-sm text-slate-900 dark:text-white shadow-lg font-medium min-w-[140px]"
              >
                <option value="all">All Devices</option>
                <option value="locked">🔒 Locked</option>
                <option value="unlocked">🔓 Unlocked</option>
              </select>
              
              {hasActiveFilters() && (
                <button
                  onClick={clearAllFilters}
                  className="flex items-center space-x-2 px-3 py-2 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-700 dark:text-red-400 rounded-xl transition-all duration-200 text-sm font-medium"
                  title="Clear all filters"
                >
                  <FilterX className="w-4 h-4" />
                  <span>Clear</span>
                </button>
              )}
            </div>
          </div>

          {/* Advanced Filters Toggle */}
          <div className="flex items-center justify-between pt-2 border-t border-slate-200/60 dark:border-slate-600/60">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className="flex items-center space-x-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
              >
                <Settings className="w-4 h-4" />
                <span>Advanced Filters</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Column Selector */}
              <div className="relative">
                <button
                  onClick={() => setShowColumnSelector(!showColumnSelector)}
                  className="flex items-center space-x-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
                >
                  <Columns className="w-4 h-4" />
                  <span>Columns</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showColumnSelector ? 'rotate-180' : ''}`} />
                </button>
                
                {showColumnSelector && (
                  <>
                    <div 
                      className="fixed inset-0 z-[100]" 
                      onClick={() => setShowColumnSelector(false)}
                    />
                    <div className="absolute top-8 right-0 z-[101] bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 p-3 min-w-[200px]"
                         style={{
                           transform: showAdvancedFilters ? 'translateY(0)' : 'translateY(-100%) translateY(-2rem)'
                         }}>
                      <div className="space-y-2">
                        <div className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3">
                          Show/Hide Columns
                        </div>
                        
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={visibleColumns.createdAt}
                            onChange={(e) => setVisibleColumns(prev => ({ ...prev, createdAt: e.target.checked }))}
                            className="rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                          />
                          <Calendar className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                          <span className="text-sm text-slate-700 dark:text-slate-300">Created Date</span>
                        </label>
                        
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={visibleColumns.updatedAt}
                            onChange={(e) => setVisibleColumns(prev => ({ ...prev, updatedAt: e.target.checked }))}
                            className="rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                          />
                          <Calendar className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                          <span className="text-sm text-slate-700 dark:text-slate-300">Updated Date</span>
                        </label>
                        
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={visibleColumns.id}
                            onChange={(e) => setVisibleColumns(prev => ({ ...prev, id: e.target.checked }))}
                            className="rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                          />
                          <Hash className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                          <span className="text-sm text-slate-700 dark:text-slate-300">Device ID</span>
                        </label>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            
            <div className="text-sm text-slate-500 dark:text-slate-400">
              Showing {filteredDevices.length} of {devices.length} devices
            </div>
          </div>

          {/* Advanced Filters Panel */}
          {showAdvancedFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4 bg-slate-50/50 dark:bg-slate-700/30 rounded-xl border border-slate-200/40 dark:border-slate-600/40">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Serial Number</label>
                <input
                  type="text"
                  placeholder="Filter by serial..."
                  value={columnFilters.serial}
                  onChange={(e) => setColumnFilters(prev => ({ ...prev, serial: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Device Name</label>
                <input
                  type="text"
                  placeholder="Filter by device name..."
                  value={columnFilters.deviceName}
                  onChange={(e) => setColumnFilters(prev => ({ ...prev, deviceName: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Owner</label>
                <input
                  type="text"
                  placeholder="Filter by owner..."
                  value={columnFilters.owner}
                  onChange={(e) => setColumnFilters(prev => ({ ...prev, owner: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Lock Status</label>
                <select
                  value={columnFilters.status}
                  onChange={(e) => setColumnFilters(prev => ({ ...prev, status: e.target.value as 'all' | 'locked' | 'unlocked' }))}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                >
                  <option value="all">All</option>
                  <option value="locked">Locked</option>
                  <option value="unlocked">Unlocked</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">ICT Status</label>
                <select
                  value={columnFilters.ictStatus}
                  onChange={(e) => setColumnFilters(prev => ({ ...prev, ictStatus: e.target.value as 'all' | 'field' | 'ict' }))}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                >
                  <option value="all">All</option>
                  <option value="field">In Field</option>
                  <option value="ict">With ICT</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Lock App Status</label>
                <select
                  value={columnFilters.lockAppStatus}
                  onChange={(e) => setColumnFilters(prev => ({ ...prev, lockAppStatus: e.target.value as 'all' | 'run' | 'not-run' }))}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                >
                  <option value="all">All</option>
                  <option value="run">App Run</option>
                  <option value="not-run">Not Run</option>
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Device List */}
      <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-2xl border border-slate-200/60 dark:border-slate-700/60 overflow-hidden shadow-lg">
        {filteredDevices.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50/80 dark:bg-slate-700/80 border-b border-slate-200/60 dark:border-slate-600/60">
                <tr>
                  <th className="text-left py-4 px-6 font-medium text-slate-900 dark:text-white w-12">
                    <button
                      onClick={handleSelectAll}
                      className="p-1 hover:bg-slate-200/60 dark:hover:bg-slate-600/60 rounded-lg transition-all duration-200"
                    >
                      {selectAll ? (
                        <CheckSquare className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      ) : (
                        <Square className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                      )}
                    </button>
                  </th>
                  <th className="text-left py-4 px-6 font-medium text-slate-900 dark:text-white">Serial Number</th>
                  <th className="text-left py-4 px-6 font-medium text-slate-900 dark:text-white">Device Name</th>
                  <th className="text-left py-4 px-6 font-medium text-slate-900 dark:text-white">
                    <button
                      onClick={() => handleSort('owner')}
                      className="flex items-center space-x-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200"
                    >
                      <span>Owner</span>
                      {sortField === 'owner' ? (
                        sortDirection === 'asc' ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )
                      ) : (
                        <ChevronDown className="w-4 h-4 opacity-30" />
                      )}
                    </button>
                  </th>
                  <th className="text-left py-4 px-6 font-medium text-slate-900 dark:text-white">Unlock Code</th>
                  <th className="text-left py-4 px-6 font-medium text-slate-900 dark:text-white">Status</th>
                  <th className="text-left py-4 px-6 font-medium text-slate-900 dark:text-white">ICT Status</th>
                  <th className="text-left py-4 px-6 font-medium text-slate-900 dark:text-white">
                    <button
                      onClick={() => handleSort('lockAppStatus')}
                      className="flex items-center space-x-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200"
                    >
                      <span>Lock App Status</span>
                      {sortField === 'lockAppStatus' ? (
                        sortDirection === 'asc' ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )
                      ) : (
                        <ChevronDown className="w-4 h-4 opacity-30" />
                      )}
                    </button>
                  </th>
                  {visibleColumns.createdAt && (
                    <th className="text-left py-4 px-6 font-medium text-slate-900 dark:text-white">
                      <button
                        onClick={() => handleSort('createdAt')}
                        className="flex items-center space-x-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200"
                      >
                        <span>Created Date</span>
                        {sortField === 'createdAt' ? (
                          sortDirection === 'asc' ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )
                        ) : (
                          <ChevronDown className="w-4 h-4 opacity-30" />
                        )}
                      </button>
                    </th>
                  )}
                  {visibleColumns.updatedAt && (
                    <th className="text-left py-4 px-6 font-medium text-slate-900 dark:text-white">
                      <button
                        onClick={() => handleSort('updatedAt')}
                        className="flex items-center space-x-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200"
                      >
                        <span>Updated Date</span>
                        {sortField === 'updatedAt' ? (
                          sortDirection === 'asc' ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )
                        ) : (
                          <ChevronDown className="w-4 h-4 opacity-30" />
                        )}
                      </button>
                    </th>
                  )}
                  {visibleColumns.id && (
                    <th className="text-left py-4 px-6 font-medium text-slate-900 dark:text-white">
                      <button
                        onClick={() => handleSort('id')}
                        className="flex items-center space-x-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200"
                      >
                        <span>Device ID</span>
                        {sortField === 'id' ? (
                          sortDirection === 'asc' ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )
                        ) : (
                          <ChevronDown className="w-4 h-4 opacity-30" />
                        )}
                      </button>
                    </th>
                  )}
                  <th className="text-left py-4 px-6 font-medium text-slate-900 dark:text-white">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200/60 dark:divide-slate-600/60">
                {filteredDevices.map((device) => (
                  <tr key={device.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-all duration-200 group">
                    <td className="py-4 px-6">
                      <button
                        onClick={() => handleSelectDevice(device.id)}
                        className="p-1 hover:bg-slate-200/60 dark:hover:bg-slate-600/60 rounded-lg transition-all duration-200"
                      >
                        {selectedDevices.has(device.id) ? (
                          <CheckSquare className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        ) : (
                          <Square className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                        )}
                      </button>
                    </td>
                    <td className="py-4 px-6">
                      <div className="font-mono text-sm font-medium text-slate-900 dark:text-white bg-slate-100 dark:bg-slate-700/50 px-2 py-1 rounded-lg inline-block">
                        {device.serial_number}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-2">
                        <div className="text-sm font-medium text-slate-900 dark:text-white">{device.device_name || '-'}</div>
                        {device.note && device.note.trim() && (
                          <div className="flex items-center justify-center w-4 h-4 bg-amber-100 dark:bg-amber-900/30 rounded-full" title="This device has a note">
                            <FileText className="w-2.5 h-2.5 text-amber-600 dark:text-amber-400" />
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="text-sm font-medium text-slate-900 dark:text-white">{device.owner_name || 'Unassigned'}</div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-2">
                        <code className={`text-xs font-mono px-2 py-1 rounded-lg bg-slate-100 dark:bg-slate-700/50 ${
                          showUnlockCodes[device.id] ? 'text-slate-900 dark:text-white' : 'text-transparent bg-slate-200 dark:bg-slate-600'
                        }`}>
                          {showUnlockCodes[device.id] ? device.unlock_code : '••••••••••••'}
                        </code>
                        <button
                          onClick={() => {
                            const isShowing = showUnlockCodes[device.id];
                            setShowUnlockCodes(prev => ({ ...prev, [device.id]: !prev[device.id] }));
                            
                            // Log viewing unlock code
                            logAction(
                              'view_unlock_code',
                              'security',
                              {
                                resourceType: 'device',
                                resourceId: device.id,
                                details: {
                                  device_name: device.device_name,
                                  serial_number: device.serial_number,
                                  action: isShowing ? 'hide' : 'show'
                                },
                                success: true
                              }
                            );
                          }}
                          className="p-1 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
                          title={showUnlockCodes[device.id] ? "Hide code" : "Show code"}
                        >
                          {showUnlockCodes[device.id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                        </button>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(device.unlock_code);
                            
                            // Log copying unlock code
                            logAction(
                              'copy_unlock_code',
                              'security',
                              {
                                resourceType: 'device',
                                resourceId: device.id,
                                details: {
                                  device_name: device.device_name,
                                  serial_number: device.serial_number
                                },
                                success: true
                              }
                            );
                          }}
                          className="p-1 text-slate-500 dark:text-slate-400 hover:text-green-600 dark:hover:text-green-400 transition-colors"
                          title="Copy to clipboard"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`inline-flex items-center px-3 py-1 rounded-lg text-xs font-medium ${
                        device.is_locked
                          ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400'
                          : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-400'
                      }`}>
                        {device.is_locked ? (
                          <>
                            <Lock className="w-3 h-3 mr-1" />
                            Locked
                          </>
                        ) : (
                          <>
                            <Unlock className="w-3 h-3 mr-1" />
                            Active
                          </>
                        )}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleToggleICT(device)}
                          className={`inline-flex items-center px-3 py-1 rounded-lg text-xs font-medium transition-all duration-200 hover:shadow-md ${
                            device.with_ict
                              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/50'
                              : 'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-400 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                          }`}
                          title={device.with_ict ? 'Move to field' : 'Move to ICT'}
                        >
                          <Building2 className="w-3 h-3 mr-1" />
                          {device.with_ict ? 'With ICT' : 'In Field'}
                        </button>
                        {device.with_ict && device.details && (
                          <div className="flex items-center space-x-1">
                            <div className="text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded">
                              {device.details.stack_number && `Stack ${device.details.stack_number}`}
                              {device.details.position_in_stack && ` Pos ${device.details.position_in_stack}`}
                            </div>
                            <div className="flex items-center space-x-1">
                              {device.details.has_charger && (
                                <div className="w-2 h-2 bg-green-500 rounded-full" title="Has charger" />
                              )}
                              {device.details.has_bag && (
                                <div title="Has bag">
                                  <Package className="w-3 h-3 text-blue-500" />
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      {lockAppStatuses[device.serial_number]?.hasRun ? (
                        <div className="flex items-center space-x-2">
                          <CheckCircle className="w-5 h-5 text-emerald-500" />
                          <span className="text-sm text-slate-700 dark:text-slate-300">
                            {lockAppStatuses[device.serial_number]?.formatted || '—'}
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <XCircle className="w-5 h-5 text-red-500" />
                          <span className="text-sm text-slate-500 dark:text-slate-400">Not Run</span>
                        </div>
                      )}
                    </td>
                    {visibleColumns.createdAt && (
                      <td className="py-4 px-6">
                        <div className="text-sm text-slate-600 dark:text-slate-400">
                          {format(new Date(device.created_at), 'dd/MM/yy hh:mm a', { timeZone: 'Australia/Adelaide' })}
                        </div>
                      </td>
                    )}
                    {visibleColumns.updatedAt && (
                      <td className="py-4 px-6">
                        <div className="text-sm text-slate-600 dark:text-slate-400">
                          {format(new Date(device.updated_at), 'dd/MM/yy hh:mm a', { timeZone: 'Australia/Adelaide' })}
                        </div>
                      </td>
                    )}
                    {visibleColumns.id && (
                      <td className="py-4 px-6">
                        <div className="font-mono text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700/50 px-2 py-1 rounded-lg inline-block">
                          {device.id}
                        </div>
                      </td>
                    )}
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => handleView(device)}
                          className="p-2 text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all duration-200"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(device)}
                          className="p-2 text-slate-500 dark:text-slate-400 hover:text-amber-600 dark:hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-all duration-200"
                          title="Edit Device"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleLock(device)}
                          className={`p-2 rounded-lg transition-all duration-200 ${
                            device.is_locked
                              ? 'text-slate-500 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20'
                              : 'text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                          }`}
                          title={device.is_locked ? 'Unlock Device' : 'Lock Device'}
                        >
                          {device.is_locked ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => handleDelete(device)}
                          className="p-2 text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200"
                          title="Delete Device"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-slate-100 dark:bg-slate-700 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Search className="w-10 h-10 text-slate-400 dark:text-slate-500" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">No devices found</h3>
            <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-md mx-auto">
              {searchTerm || filterStatus !== 'all' 
                ? 'Try adjusting your search or filter criteria'
                : 'Get started by adding your first device'
              }
            </p>
            {!searchTerm && filterStatus === 'all' && (
              <div className="flex justify-center space-x-3">
                <button
                  onClick={handleAdd}
                  className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl font-medium"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add First Device</span>
                </button>
                <Link
                  to="/bulk-import"
                  className="inline-flex items-center space-x-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-6 py-3 rounded-xl hover:from-emerald-700 hover:to-teal-700 transition-all duration-200 shadow-lg hover:shadow-xl font-medium"
                >
                  <Upload className="w-4 h-4" />
                  <span>Bulk Import</span>
                </Link>
              </div>
            )}
          </div>
        )}
      </div>

      {isLockMessageManagerOpen && (
        <div className="modal-overlay">
          <div className="w-full max-w-7xl h-[90vh] overflow-hidden bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-2xl">
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white">Lock Message Templates</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">Create, edit, and set default templates for lock screen messaging.</p>
              </div>
              <button
                onClick={() => {
                  setIsLockMessageManagerOpen(false);
                  resetLockMessageForm();
                }}
                className="p-2 rounded-lg text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-0 h-[calc(90vh-80px)] overflow-hidden">
              <div className="p-6 xl:border-r border-slate-200 dark:border-slate-700 space-y-4 overflow-y-auto">
                <h4 className="text-lg font-semibold text-slate-900 dark:text-white">
                  {editingLockMessageId ? 'Edit Template' : 'Create Template'}
                </h4>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Title</label>
                  <input
                    type="text"
                    value={lockMessageForm.title}
                    onChange={(e) => setLockMessageForm((prev) => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                    placeholder="Device Locked for Recall"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Line 1</label>
                  <textarea
                    value={lockMessageForm.line1}
                    onChange={(e) => setLockMessageForm((prev) => ({ ...prev, line1: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Line 2</label>
                  <textarea
                    value={lockMessageForm.line2}
                    onChange={(e) => setLockMessageForm((prev) => ({ ...prev, line2: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Line 3</label>
                  <textarea
                    value={lockMessageForm.line3}
                    onChange={(e) => setLockMessageForm((prev) => ({ ...prev, line3: e.target.value }))}
                    rows={2}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Footer</label>
                  <input
                    type="text"
                    value={lockMessageForm.footer}
                    onChange={(e) => setLockMessageForm((prev) => ({ ...prev, footer: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                    placeholder="Aldinga Payinthi College ICT Services"
                  />
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <button
                    onClick={handleSaveLockMessage}
                    disabled={lockMessageSaving || !lockMessageForm.title.trim() || !lockMessageForm.line1.trim()}
                    className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400 text-white font-medium"
                  >
                    {lockMessageSaving ? 'Saving...' : editingLockMessageId ? 'Update Template' : 'Create Template'}
                  </button>
                  {editingLockMessageId && (
                    <button
                      onClick={resetLockMessageForm}
                      className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300"
                    >
                      Cancel Edit
                    </button>
                  )}
                </div>
              </div>

              <div className="p-6 space-y-3 overflow-hidden flex flex-col">
                <div>
                  <h5 className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-2">Live WPF Preview</h5>
                  <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700">
                    <div
                      className="relative h-[430px] bg-cover bg-center"
                      style={{
                        backgroundImage: 'url(/lock-preview-bg.jpg), radial-gradient(circle at 20% 20%, #6b7280 0%, #374151 38%, #111827 100%)'
                      }}
                    >
                      <div className="absolute inset-0 bg-black/65" />

                      <div className="absolute inset-0 flex items-center justify-center p-4">
                        <div className="w-full max-w-[700px] bg-white/80 border border-slate-300 px-8 py-7 text-center shadow-xl">
                          {previewTitle ? (
                            <h3 className="text-2xl font-bold text-black mb-3 leading-tight">{previewTitle}</h3>
                          ) : (
                            <div className="text-sm text-slate-500 mb-3">Title will appear here</div>
                          )}

                          {previewLine1 && <p className="text-[15px] text-black leading-relaxed mb-3">{previewLine1}</p>}
                          {previewLine2 && <p className="text-[15px] text-black leading-relaxed mb-3">{previewLine2}</p>}
                          {previewLine3 && <p className="text-[15px] font-semibold text-black leading-relaxed mb-2">{previewLine3}</p>}
                          {previewFooter && <p className="text-[13px] italic text-black leading-relaxed mb-4">{previewFooter}</p>}

                          <div className="flex flex-col items-center gap-2">
                            <input
                              disabled
                              value=""
                              placeholder=""
                              className="w-[260px] h-8 border border-slate-400 bg-white"
                            />
                            <button
                              disabled
                              className="px-6 py-1 border border-slate-500 bg-slate-100 text-black text-sm"
                            >
                              Unlock
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                    Preview background uses /lock-preview-bg.jpg if available in public folder.
                  </p>
                </div>

                <div className="flex items-center justify-between pt-1">
                  <h4 className="text-lg font-semibold text-slate-900 dark:text-white">Existing Templates</h4>
                </div>

                <div className="space-y-3 overflow-y-auto pr-1">
                  {lockMessagesLoading ? (
                    <p className="text-sm text-slate-500 dark:text-slate-400">Loading templates...</p>
                  ) : lockMessages.length === 0 ? (
                    <p className="text-sm text-slate-500 dark:text-slate-400">No lock message templates created yet.</p>
                  ) : (
                    lockMessages.map((message) => (
                    <div key={message.id} className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 bg-slate-50/60 dark:bg-slate-800/40">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div>
                          <h5 className="font-semibold text-slate-900 dark:text-white">{message.title}</h5>
                          {message.is_default && (
                            <span className="inline-flex mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                              Default
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleEditLockMessage(message.id)}
                            className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteLockMessage(message.id)}
                            className="px-2 py-1 text-xs rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                          >
                            Delete
                          </button>
                        </div>
                      </div>

                      <div className="text-sm text-slate-700 dark:text-slate-300 space-y-1">
                        <p>{message.line1}</p>
                        {message.line2 && <p>{message.line2}</p>}
                        {message.line3 && <p>{message.line3}</p>}
                        {message.footer && <p className="italic">{message.footer}</p>}
                      </div>

                      {!message.is_default && (
                        <button
                          onClick={() => handleSetDefaultLockMessage(message.id)}
                          className="mt-3 px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          Set As Default
                        </button>
                      )}
                    </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {lockConfigModal.isOpen && (
        <div className="modal-overlay">
          <div className="w-full max-w-lg bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-2xl">
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white">Select Lock Message Template</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Applying lock to {lockConfigModal.deviceIds.length} device{lockConfigModal.deviceIds.length > 1 ? 's' : ''}
                </p>
              </div>
              <button
                onClick={() => setLockConfigModal({ isOpen: false, deviceIds: [] })}
                className="p-2 rounded-lg text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {lockMessages.length === 0 ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300 p-4 text-sm">
                  No templates available. Create one in Lock Messages before locking devices.
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Template</label>
                  <select
                    value={selectedLockMessageId}
                    onChange={(e) => setSelectedLockMessageId(Number(e.target.value))}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                  >
                    {lockMessages.map((message) => (
                      <option key={message.id} value={message.id}>
                        {message.title}{message.is_default ? ' (Default)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  onClick={() => setLockConfigModal({ isOpen: false, deviceIds: [] })}
                  className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLockWithTemplate}
                  disabled={lockConfigLoading || !selectedLockMessageId || lockMessages.length === 0}
                  className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 disabled:bg-slate-400 text-white font-medium"
                >
                  {lockConfigLoading ? 'Locking...' : `Lock ${lockConfigModal.deviceIds.length} Device${lockConfigModal.deviceIds.length > 1 ? 's' : ''}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Device Modal */}
      <DeviceModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setOriginalDevice(null); // Clear original device when closing
        }}
        device={selectedDevice}
        mode={modalMode}
        onSave={handleSave}
        originalDevice={originalDevice}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() => setConfirmationModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmationModal.onConfirm}
        title={getConfirmationTitle()}
        message={getConfirmationMessage()}
        type={confirmationModal.type}
        deviceCount={selectedDevices.size}
        loading={actionLoading}
      />
    </div>
  );
}
